<i18n src="@/i18n/shared/support/show.json"></i18n>
<i18n src="@/i18n/form.json"></i18n>
<template>
	<Page
		:title="$t('support.title', [form.id])"
		icon-back
		:titleLoading="isBgLoading"
		:loading="isLoading">
		<template #after-title>
			<div class="ml-auto flex space-x-3">
				<SupportTicketDeleteButton :support_ticket="form" @deleted="handleDeleted"/>
				<!-- Editar !-->
				<EditButton :to="{name: 'tickets.edit', params: {id: form.id}}"/>
				<!-- Resumo da remessa, com o link para a remessa completa dentro !-->
				<ShipmentSummaryButton v-if="form.shipment_id" :shipment-id="form.shipment_id"/>
			</div>
		</template>
		<Card title="Ticket">
			<section aria-labelledby="message-heading" class="relative flex h-full min-w-0 flex-grow flex-col overflow-hidden bg-gray-100">
				<!-- Cabeçalho do chamado !-->
				<SupportTicketConversationHeader :ticket="form">
					<!-- Ações !-->
					<template v-if="isOpen || canArchive">
						<div class="mt-4 flex flex-wrap items-center gap-3 border-t border-gray-100 pt-4">
							<!-- Responder !-->
							<SupportTicketReplyButton v-if="isOpen" @click="handleReply"/>
							<span class="ml-auto flex items-center gap-3">
								<!-- Arquivar !-->
								<SupportTicketArchiveButton :ticket="form" @archived="handleArchived"/>
								<!-- Concluir !-->
								<SupportTicketCloseButton :ticket="form" @closed="handleClosed"/>
							</span>
						</div>
					</template>
				</SupportTicketConversationHeader>
				<!-- Conversa !-->
				<div class="min-h-0 flex-1 overflow-y-auto">
					<SupportTicketThread wide :messages="form.messages" :requester-id="$data_get(form, 'created_by.id')">
						<template #composer>
							<!-- Resposta !-->
							<template v-if="showReplyForm">
								<div ref="replyForm">
									<SupportTicketComposer
										ref="replyBody"
										v-model="form.body"
										:file.sync="file"
										:author-label="$data_get($profile, 'name', 'Você')"
										:recipients="recipientNames"
										@send="handleSaveReply"
										@cancel="handleCancelReply"/>
								</div>
							</template>
							<!-- Chamado encerrado !-->
							<template v-else-if="!isOpen">
								<p class="mt-8 text-center text-sm text-gray-500">Este ticket está encerrado. Não é possível responder.</p>
							</template>
						</template>
					</SupportTicketThread>
				</div>
			</section>
		</Card>
	</Page>
</template>

<script>
import Mixin from "./mixins/show.js"

import Page from "@/components/Page.vue"
import Card from "@/components/Card.vue"
import FormLine from "@/components/FormLine.vue"
import FormInput from "@/components/FormInput.vue"
import FormGroup from "@/components/FormGroup.vue"
import CreateButton from "@/components/CreateButton.vue"
import EditButton from "@/components/EditButton.vue"
import Loading from "@/components/Loading.vue"

import SupportTicketFormPartial from "./partials/SupportTicketFormPartial.vue"
import TicketNavPartial from "./partials/TicketNavPartial.vue"

import SupportTicketConversationHeader from "@/domain/support_tickets/SupportTicketConversationHeader.vue"
import SupportTicketThread from "@/domain/support_tickets/SupportTicketThread.vue"
import SupportTicketComposer from "@/domain/support_tickets/SupportTicketComposer.vue"
import SupportTicketReplyButton from "@/domain/support_tickets/SupportTicketReplyButton.vue";
import SupportTicketCloseButton from "@/domain/support_tickets/SupportTicketCloseButton.vue";
import SupportTicketArchiveButton from "@/domain/support_tickets/SupportTicketArchiveButton.vue";
import SupportTicketDeleteButton from "@/domain/support_tickets/SupportTicketDeleteButton.vue";
import ShipmentSummaryButton from "@/domain/shipments/ShipmentSummaryButton.vue";

import Http from "@/config/axios.js"

export default {
	name: "ShowPage",
	mixins: [Mixin],
	props: {
		id: [Number, String]
	},
	components: {
		SupportTicketConversationHeader,
		SupportTicketThread,
		SupportTicketComposer,
		Card,
		Page,
		FormGroup,
		FormLine,
		FormInput,
		SupportTicketFormPartial,
		CreateButton,
		TicketNavPartial,
		Loading,
		SupportTicketDeleteButton,
		EditButton,
		ShipmentSummaryButton,
		SupportTicketArchiveButton,
		SupportTicketCloseButton,
		SupportTicketReplyButton,
	},
	data() {
		return {
			showForm: false,
			showDetails: false,
			showReplyForm: false,
			file: null,
			replyForm: {
				form: null,
				attachments: []
			}
		}
	},
	computed: {
		/**
		 * Chamado aberto ou aguardando: aceita resposta
		 * @returns {boolean}
		 */
		isOpen() {
			return this.form.support_ticket_status == 10 || this.form.support_ticket_status == 20
		},
		/**
		 * Chamado fechado: aceita ser arquivado
		 * @returns {boolean}
		 */
		canArchive() {
			return this.form.support_ticket_status == 0
		},
		/**
		 * Nomes dos destinatários, para as linhas "para" da conversa
		 * @returns {Array<string>}
		 */
		recipientNames() {
			return (this.form?.addresses ?? []).map(address => {
				return typeof address.label === "string" ? address.label : this.$localize(address.label)
			})
		},
		/**
		 * Has tickets
		 * @returns {boolean}
		 */
		hasTickets() {
			return this.page?.data?.length > 0
		},
		/**
		 * Destination addresses
		 * @returns {string | undefined}
		 */
		destinationAddresses() {
			return this.ticket?.addresses?.map(address => address.label).join(', ')
		},
		addressList() {
			return this.ticket?.addresses ?? []
		}
	},
	methods: {
		handleCancel() {
			this.$router.back()
		},
		handleDeleted() {
			this.$router.back()
		},
		/**
		 * Handle close ticket
		 */
		handleCloseTicket() {
			this.ticket = {}
			this.$emit('close')
		},
		/**
		 * Handle select ticket
		 * @param evt
		 */
		handleSelectTicket(evt) {
			this.load(evt.id)
		},
		handleShowDetails() {
			this.showDetails = !this.showDetails
		},
		/**
		 * Handle archive
		 * @returns {Promise<void>}
		 */
		async handleArchived() {
			this.$alertSuccess('Ticket arquivado com sucesso!')
			this.load(this.form.id)
		},
		/**
		 * Handle closed
		 * @returns {Promise<void>}
		 */
		async handleClosed() {
			this.$alertSuccess('Ticket fechado com sucesso!')
			this.load(this.form.id)
		},
		/**
		 * Handler reply
		 */
		handleReply() {
			this.showReplyForm = true
			this.$nextTick(() => {
				this.$refs.replyForm?.scrollIntoView({behavior: 'smooth', block: 'end', inline: 'nearest'})
				// Fix for scroll animation
				setTimeout(() => this.$refs.replyBody?.focus(), 500)
			})
		},
		/**
		 * Handle save reply
		 * @returns {Promise<void>}
		 */
		async handleSaveReply() {
			if (await this.reply()) {
				this.$alertSuccess('Messangem enviada com sucesso!')
			}
		},
		/**
		 * Handle cancel reply
		 */
		handleCancelReply() {
			this.form.body = ""
			this.file = null
			this.showReplyForm = false
		},
		handleCreate() {
			this.showForm = true
		},
		handleShowForm() {
			this.showForm = true
		},
		handleCreateTicket() {
			this.$emit('create')
		}
	},
	mounted() {
		this.init()
	}
}
</script>